import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { listRoster } from "@/lib/api";
import { SearchableSelect } from "@/components/SearchableSelect";

// Legacy roleType → new skill mapping so callers can migrate at their leisure.
const ROLE_TO_SKILL = {
  sales_rep: "sales",
  accountability_coach: "accountability",
  lead_nurture_exec: "lead_nurture",
};

/**
 * A text input backed by the Team Roster. Users can pick from the dropdown or type freely.
 * Preferred prop: `skill` (one of "sales" | "accountability" | "lead_nurture" | "SPT" | "PT").
 * Legacy `roleType` prop still accepted for backward compat.
 */
export function RosterAutocomplete({
  skill,
  roleType,
  value = "",
  onChange,
  placeholder = "Type or pick from roster…",
  testid,
}) {
  const effectiveSkill = skill || (roleType ? ROLE_TO_SKILL[roleType] || roleType : undefined);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    listRoster(effectiveSkill, true)
      .then((r) => {
        if (!cancelled) setMembers(r || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [effectiveSkill]);

  const suggestions = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.phone || "").toLowerCase().includes(q),
    );
  }, [members, value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} className="relative">
      <Input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className="bg-[#0a0a0a] border-[#262626]"
        data-testid={testid}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 rounded-lg border border-[#262626] bg-[#0f0f0f] shadow-xl max-h-56 overflow-y-auto"
          data-testid={testid ? `${testid}-suggestions` : undefined}
        >
          {suggestions.slice(0, 10).map((m) => (
            <button
              type="button"
              key={m.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(m.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#1a1a1a] transition-colors"
              data-testid={testid ? `${testid}-option-${m.id}` : undefined}
            >
              <div className="text-zinc-100">{m.name}</div>
              {(m.email || m.phone) && (
                <div className="text-[10px] text-zinc-500">{[m.email, m.phone].filter(Boolean).join(" · ")}</div>
              )}
            </button>
          ))}
          {members.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500">
              No entries in roster yet — add one in Settings → Team Roster.
            </div>
          )}
        </div>
      )}
      {focused && members.length === 0 && (
        <div className="text-[10px] text-amber-400/80 mt-1">
          Tip: add roster entries in Settings → Team Roster so this suggests names.
        </div>
      )}
    </div>
  );
}


/**
 * Pick-only version (Oct 2026): names come from the Team Roster (User Management) so each person is
 * always spelled one way. Free typing split one rep's sales across "Suraj" and "Suraj Shankar", which
 * also split their incentive slab. A saved name that isn't on the roster still shows so it can be fixed.
 */
export function RosterSelect({ skill, roleType, value = "", onChange, placeholder = "Pick from the team…", testid }) {
  const effectiveSkill = skill || (roleType ? ROLE_TO_SKILL[roleType] || roleType : undefined);
  const [members, setMembers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listRoster(effectiveSkill, true).then((r) => { if (!cancelled) setMembers(r || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [effectiveSkill]);
  const options = useMemo(() => {
    const names = [...new Set(members.map((m) => (m.name || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const opts = names.map((n) => ({ value: n, label: n }));
    const v = (value || "").trim();
    if (v && !names.includes(v)) opts.unshift({ value: value, label: `${v} (not in User Management)`, hint: "pick the right person" });
    return opts;
  }, [members, value]);
  return <SearchableSelect options={options} value={value || ""} onChange={(v) => onChange(v || "")}
    placeholder={placeholder} emptyLabel="— none —" testid={testid} />;
}
